import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { MerchantDocument, MerchantProfilePatch, MerchantRow, TransactionRow, UiMerchantStatus } from '../../api/types';
import { useAuth } from '../../context/AuthContext';
import { EditFeesModal } from '../modals/EditFeesModal';
import {
  EditMerchantProfileModal,
  type ProfileEditSection,
} from '../modals/EditMerchantProfileModal';
import { TransactionDetailModal } from '../transactions/TransactionDetailModal';
import {
  allowedQuickStatuses,
  canAccessKyb,
  canChangeMerchantStatus,
  canEditMerchantFees,
  canEditMerchantProfileBlocks,
  canEditSettlementWallet,
  canExportTransactions,
  canViewWalletChangelog,
  isCeo,
} from '../../utils/roles';
import { downloadCsv, rowsToCsv } from '../../utils/exportCsv';
import type { WalletChangelogEntry } from '../../api/types';
import {
  badgeClass,
  btnClass,
  mapDbToUi,
  statusLabel,
} from '../../utils/merchantStatus';
import { DocumentPreview } from './DocumentPreview';
import { MerchantOverviewBlocks } from './MerchantOverviewBlocks';


type MerchantProfileProps = {
  merchant: MerchantRow;
  onBack: () => void;
  onUpdated: (merchant: MerchantRow) => void;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function txStatusBadge(status: string): string {
  if (status === 'confirmed') return 'badge badge-success';
  if (status === 'failed' || status === 'rejected') return 'badge badge-blocked';
  return 'badge badge-pending';
}

export function MerchantProfile({ merchant: initial, onBack, onUpdated }: MerchantProfileProps) {
  const { role } = useAuth();
  const ceo = isCeo(role);
  const showDocuments = canAccessKyb(role);
  const showChangelog = canViewWalletChangelog(role);
  const profileTabs = [
    'overview',
    ...(showDocuments ? ['documents'] : []),
    ...(showChangelog ? ['wallet-changelog'] : []),
    'financials',
  ] as const;
  type Tab = (typeof profileTabs)[number];

  const merchantId = initial.id;
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  const [merchant, setMerchant] = useState(initial);
  const [tab, setTab] = useState<Tab>('overview');
  const [balance, setBalance] = useState<string | null>(null);
  const [currency, setCurrency] = useState('USDT');
  const [documents, setDocuments] = useState<MerchantDocument[]>([]);
  const [selectedDoc, setSelectedDoc] = useState<MerchantDocument | null>(null);
  const [transactions, setTransactions] = useState<TransactionRow[]>([]);
  const [selectedTx, setSelectedTx] = useState<TransactionRow | null>(null);
  const [changelog, setChangelog] = useState<WalletChangelogEntry[]>([]);
  const [loadingChangelog, setLoadingChangelog] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loadingTx, setLoadingTx] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [feesOpen, setFeesOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [profileSection, setProfileSection] = useState<ProfileEditSection>('legal');

  const uiStatus = mapDbToUi(merchant.status);
  const canStatus = ceo || canChangeMerchantStatus(role);
  const canFees = ceo || canEditMerchantFees(role);
  const canEditBlocks = canEditMerchantProfileBlocks(role);
  const canSettlement = canEditSettlementWallet(role);
  const canExportTx = canExportTransactions(role);
  const quickStatuses = allowedQuickStatuses(role);

  const refreshMerchant = useCallback(async () => {
    try {
      const res = await api.getMerchant(merchantId);
      setMerchant(res.merchant);
      setBalance(res.balance_display ?? null);
      setCurrency(res.currency ?? 'USDT');
      onUpdatedRef.current(res.merchant);
    } catch (e) {
      setError(getApiErrorMessage(e));
    }
  }, [merchantId]);

  useEffect(() => {
    setMerchant(initial);
    setTab('overview');
    setError(null);
    setBalance(null);
  }, [initial]);

  useEffect(() => {
    let cancelled = false;
    setLoadingDetail(true);
    void (async () => {
      try {
        const res = await api.getMerchant(merchantId);
        if (cancelled) return;
        setMerchant(res.merchant);
        setBalance(res.balance_display ?? null);
        setCurrency(res.currency ?? 'USDT');
        onUpdatedRef.current(res.merchant);
      } catch (e) {
        if (!cancelled) setError(getApiErrorMessage(e));
      } finally {
        if (!cancelled) setLoadingDetail(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [merchantId]);

  useEffect(() => {
    if (tab !== 'documents') return;
    setLoadingDocs(true);
    void api
      .getMerchantDocuments(merchant.id)
      .then((res) => {
        setDocuments(res.documents);
        setSelectedDoc(res.documents[0] ?? null);
      })
      .catch((e) => setError(getApiErrorMessage(e)))
      .finally(() => setLoadingDocs(false));
  }, [tab, merchant.id]);

  useEffect(() => {
    if (tab !== 'wallet-changelog') return;
    setLoadingChangelog(true);
    void api
      .getMerchantWalletChangelog(merchant.id, 200)
      .then((res) => setChangelog(res.changelog))
      .catch((e) => setError(getApiErrorMessage(e)))
      .finally(() => setLoadingChangelog(false));
  }, [tab, merchant.id]);

  useEffect(() => {
    if (tab !== 'financials') return;
    setLoadingTx(true);
    void api
      .listTransactions(50, merchant.id)
      .then((res) => setTransactions(res.transactions))
      .catch((e) => setError(getApiErrorMessage(e)))
      .finally(() => setLoadingTx(false));
  }, [tab, merchant.id]);

  const applyStatus = async (next: UiMerchantStatus) => {
    if (!canStatus) return;
    setStatusSaving(true);
    setError(null);
    try {
      const res = await api.patchMerchantStatus(merchant.id, next);
      if (res.merchant) {
        setMerchant(res.merchant);
        onUpdated(res.merchant);
      } else {
        await refreshMerchant();
      }
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setStatusSaving(false);
    }
  };

  const saveFees = async (merchantId: string, feePercent: number) => {
    const res = await api.patchMerchantFees(merchantId, feePercent);
    if (res.merchant) {
      setMerchant(res.merchant);
      onUpdated(res.merchant);
    }
  };

  const saveProfile = async (merchantId: string, body: MerchantProfilePatch) => {
    if (body.solana_wallet) {
      const res = await api.patchMerchantSettlementWallet(
        merchantId,
        body.solana_wallet,
        body.change_reason ?? '',
        body.staff_password ?? '',
      );
      if (res.merchant) {
        setMerchant(res.merchant);
        onUpdated(res.merchant);
      }
      return;
    }
    const res = await api.patchMerchantProfile(merchantId, body);
    if (res.merchant) {
      setMerchant(res.merchant);
      onUpdated(res.merchant);
    }
  };

  return (
    <div className="merchant-profile">
      <div className="merchant-profile-toolbar">
        <button type="button" className="btn-outline btn-sm profile-back" onClick={onBack}>
          <ArrowLeft size={16} aria-hidden /> Back to directory
        </button>
        <div className="merchant-profile-title-wrap">
          <h2 className="merchant-profile-title">{merchant.name}</h2>
          <span className={badgeClass(uiStatus)}>{statusLabel(uiStatus)}</span>
        </div>
      </div>

      {error ? <p className="form-error section-error">{error}</p> : null}

      <div className="profile-tabs">
        {profileTabs.map((t) => (
          <button
            key={t}
            type="button"
            className={`profile-tab${tab === t ? ' active' : ''}`}
            onClick={() => setTab(t)}
          >
            {t === 'overview'
              ? 'Overview'
              : t === 'documents'
                ? 'Documents'
                : t === 'wallet-changelog'
                  ? 'Wallet changelog'
                  : 'Financials'}
          </button>
        ))}
      </div>

      {tab === 'overview' ? (
        <div className="card profile-overview">
          <MerchantOverviewBlocks
                merchant={merchant}
                balance={balance}
                currency={currency}
                role={role}
                canEditAccount={canEditBlocks}
                onEditAccount={() => setFeesOpen(true)}
                canEditLegal={canEditBlocks}
                onEditLegal={() => {
                  setProfileSection('legal');
                  setProfileOpen(true);
                }}
                canEditRisk={canEditBlocks}
                onEditRisk={() => {
                  setProfileSection('risk');
                  setProfileOpen(true);
                }}
                canEditTechnical={canEditBlocks}
                onEditTechnical={() => {
                  setProfileSection('technical');
                  setProfileOpen(true);
                }}
                canEditSettlement={canSettlement}
                onEditSettlement={() => {
                  setProfileSection('settlement');
                  setProfileOpen(true);
                }}
              />
              <div className="section-title profile-status-heading">Quick status</div>
              <div className="status-toggle">
                {quickStatuses.map((st) => (
                  <button
                    key={st}
                    type="button"
                    className={btnClass(st, uiStatus)}
                    disabled={!canStatus || statusSaving}
                    onClick={() => void applyStatus(st)}
                  >
                    {statusLabel(st)}
                  </button>
                ))}
              </div>
          {loadingDetail ? (
            <p className="modal-muted profile-balance-loading">Loading balance…</p>
          ) : null}
        </div>
      ) : null}

      {tab === 'documents' ? (
        <div className="profile-documents-layout">
          <div className="card profile-doc-list">
            <div className="section-title">KYB documents</div>
            {loadingDocs ? (
              <p className="modal-muted">Loading…</p>
            ) : (
              <ul className="doc-list">
                {documents.map((doc) => (
                  <li key={doc.type}>
                    <button
                      type="button"
                      className={`doc-list-item${selectedDoc?.type === doc.type ? ' active' : ''}`}
                      onClick={() => setSelectedDoc(doc)}
                    >
                      <span className="cell-primary">{doc.label}</span>
                      <span className="cell-muted">{doc.url ? 'Available' : 'Missing'}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="card profile-doc-viewer">
            <div className="section-title">{selectedDoc?.label ?? 'Preview'}</div>
            <DocumentPreview document={selectedDoc} />
          </div>
        </div>
      ) : null}

      {tab === 'wallet-changelog' ? (
        <div className="card">
          <div className="console-header" style={{ marginBottom: 16 }}>
            <div className="section-title" style={{ margin: 0 }}>
              Settlement wallet history
            </div>
            {changelog.length > 0 ? (
              <button
                type="button"
                className="btn-outline btn-sm"
                onClick={() => {
                  const headers = [
                    'created_at',
                    'previous_wallet',
                    'new_wallet',
                    'changed_by_type',
                    'changed_by_email',
                    'ip_address',
                  ] as const;
                  const rows = changelog.map((e) =>
                    headers.map((h) => (e as Record<string, unknown>)[h] ?? ''),
                  );
                  downloadCsv(
                    `wallet-changelog-${merchant.id.slice(0, 8)}.csv`,
                    rowsToCsv(headers, rows),
                  );
                }}
              >
                Export CSV
              </button>
            ) : null}
          </div>
          {loadingChangelog ? (
            <p className="modal-muted">Loading changelog…</p>
          ) : changelog.length === 0 ? (
            <p className="modal-muted">No wallet changes recorded yet.</p>
          ) : (
            <div className="table-wrap">
              <div className="table-head data-table-head">
                <div className="th">When</div>
                <div className="th">Actor</div>
                <div className="th">Previous</div>
                <div className="th">New</div>
              </div>
              {changelog.map((e) => (
                <div key={e.id} className="table-row data-table-row">
                  <div>{formatDate(e.created_at)}</div>
                  <div>
                    {e.changed_by_type} · {e.changed_by_email ?? '—'}
                  </div>
                  <div className="cell-mono" style={{ fontSize: 11 }}>
                    {e.previous_wallet.slice(0, 12)}…
                  </div>
                  <div className="cell-mono" style={{ fontSize: 11 }}>
                    {e.new_wallet.slice(0, 12)}…
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : null}

      {tab === 'financials' ? (
        <>
          {canExportTx ? (
            <div className="console-header" style={{ marginBottom: 12 }}>
              <p className="console-sub" style={{ margin: 0 }}>
                Merchant-scoped ledger
              </p>
              <button
                type="button"
                className="btn-outline btn-sm"
                disabled={loadingTx}
                onClick={() => {
                  void api.exportTransactionsCsv({ merchant_id: merchant.id, limit: 5000 }).then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `transactions-${merchant.id.slice(0, 8)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }).catch((e) => setError(getApiErrorMessage(e)));
                }}
              >
                Export CSV
              </button>
            </div>
          ) : null}
        <div className="table-wrap">
          <div className="table-head data-table-head tx-head-ledger tx-head-ledger-compact">
            <div className="th col-id">Signature</div>
            <div className="th col-time">Time</div>
            <div className="th col-gross">Gross</div>
            <div className="th col-net">Net</div>
            <div className="th col-status">Status</div>
          </div>
          {loadingTx ? (
            <div className="table-row">
              <div className="col-span-all">Loading transactions…</div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="table-row">
              <div className="col-span-all">No transactions for this merchant</div>
            </div>
          ) : (
            transactions.map((tx) => (
              <div
                key={tx.id}
                className="table-row data-table-row tx-row-clickable tx-row-compact"
                onClick={() => setSelectedTx(tx)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setSelectedTx(tx);
                }}
                role="button"
                tabIndex={0}
              >
                <div className="col-id cell-mono">{tx.signature.slice(0, 16)}…</div>
                <div className="col-time">{formatDate(tx.created_at)}</div>
                <div className="col-gross">
                  {tx.gross_amount ?? tx.amount_display} {tx.currency}
                </div>
                <div className="col-net profile-balance">
                  {tx.net_amount ?? tx.amount_display} {tx.currency}
                </div>
                <div className="col-status">
                  <span className={txStatusBadge(tx.status)}>{tx.status}</span>
                </div>
              </div>
            ))
          )}
        </div>
        </>
      ) : null}

      <EditFeesModal
        merchant={feesOpen ? merchant : null}
        open={feesOpen}
        onClose={() => setFeesOpen(false)}
        onSave={saveFees}
      />
      <EditMerchantProfileModal
        merchant={profileOpen ? merchant : null}
        section={profileSection}
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
        onSave={saveProfile}
      />
      <TransactionDetailModal
        transaction={selectedTx}
        open={Boolean(selectedTx)}
        onClose={() => setSelectedTx(null)}
      />
    </div>
  );
}
